import { useState, useEffect } from "react";
import { shiftPatternApi, breakTypeApi, businessHoursApi } from "../lib/api";
import { formatTime, calculateDurationHours } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Clock, Plus, Pencil, Trash2, Coffee, Utensils } from "lucide-react";
import { toast } from "sonner";
import { TimePicker } from "../components/ui/time-picker";

// Icon mapping for breaks
const BREAK_ICON_MAP = {
  coffee: Coffee,
  utensils: Utensils,
  clock: Clock,
};

const getBreakIcon = (iconName) => BREAK_ICON_MAP[iconName] || Coffee;

const Shifts = () => {
  const [patterns, setPatterns] = useState([]);
  const [breakTypes, setBreakTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snapInterval, setSnapInterval] = useState(15);
  const [form, setForm] = useState({
    name: "",
    start_time: "09:00",
    end_time: "17:00",
    break_type_ids: []
  });

  const fetchData = async () => {
    try {
      const [patternsRes, breaksRes, hoursRes] = await Promise.all([
        shiftPatternApi.getAll(),
        breakTypeApi.getAll(),
        businessHoursApi.get()
      ]);
      setPatterns(patternsRes.data);
      setBreakTypes(breaksRes.data);
      setSnapInterval(hoursRes.data?.snap_interval || 15);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load shift patterns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDialog = (pattern = null) => {
    if (pattern) {
      setEditingPattern(pattern);
      setForm({
        name: pattern.name,
        start_time: pattern.start_time,
        end_time: pattern.end_time,
        break_type_ids: pattern.break_type_ids || []
      });
    } else {
      setEditingPattern(null);
      setForm({
        name: "",
        start_time: "09:00",
        end_time: "17:00",
        break_type_ids: []
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Pattern name is required");
      return;
    }

    try {
      if (editingPattern) {
        await shiftPatternApi.update(editingPattern.id, form);
        toast.success("Shift pattern updated");
      } else {
        await shiftPatternApi.create(form);
        toast.success("Shift pattern created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving shift pattern:", error);
      toast.error("Failed to save shift pattern");
    }
  };

  const handleDelete = async (pattern) => {
    if (!confirm(`Delete "${pattern.name}" shift pattern?`)) return;

    try {
      await shiftPatternApi.delete(pattern.id);
      toast.success("Shift pattern deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting shift pattern:", error);
      toast.error("Failed to delete shift pattern");
    }
  };

  const toggleBreakType = (breakTypeId) => {
    setForm(prev => ({
      ...prev,
      break_type_ids: prev.break_type_ids.includes(breakTypeId)
        ? prev.break_type_ids.filter(id => id !== breakTypeId)
        : [...prev.break_type_ids, breakTypeId]
    }));
  };

  const getBreakTypeNames = (breakTypeIds) => {
    if (!breakTypeIds || breakTypeIds.length === 0) return "None";
    return breakTypeIds
      .map(id => breakTypes.find(bt => bt.id === id)?.name)
      .filter(Boolean)
      .join(", ");
  };

  const shiftDuration = calculateDurationHours(form.start_time, form.end_time);

  return (
    <div className="space-y-6" data-testid="shifts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Shift Patterns</h2>
          <p className="text-muted-foreground">Create predetermined shift patterns for quick scheduling</p>
        </div>
        <Button onClick={() => openDialog()} data-testid="add-pattern-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Pattern
        </Button>
      </div>

      {/* Patterns Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Shift Patterns
          </CardTitle>
          <CardDescription>
            Define common shift times that can be quickly applied when scheduling employees
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : patterns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Clock className="w-8 h-8 mb-2 opacity-50" />
              <p>No shift patterns configured</p>
              <p className="text-sm">Add patterns like &quot;Morning Shift&quot; or &quot;Evening Shift&quot;</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Start Time</TableHead>
                  <TableHead>End Time</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Breaks</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patterns.map((pattern) => {
                  const duration = calculateDurationHours(pattern.start_time, pattern.end_time);
                  return (
                    <TableRow key={pattern.id} data-testid={`pattern-row-${pattern.id}`}>
                      <TableCell className="font-medium">{pattern.name}</TableCell>
                      <TableCell className="tabular-nums">{formatTime(pattern.start_time)}</TableCell>
                      <TableCell className="tabular-nums">{formatTime(pattern.end_time)}</TableCell>
                      <TableCell>{duration.toFixed(1)}h</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {getBreakTypeNames(pattern.break_type_ids)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog(pattern)}
                            data-testid={`edit-pattern-${pattern.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(pattern)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-pattern-${pattern.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle>Tips</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• <strong>Shift patterns</strong> allow you to quickly apply common shift times when scheduling</p>
          <p>• <strong>Breaks</strong> selected here will be automatically added when using this pattern</p>
          <p>• Break times can still be adjusted when the shift is created to avoid overlapping employee breaks</p>
          <p>• Pay rates are set per employee, not per shift pattern</p>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <DialogTitle>
              {editingPattern ? "Edit Shift Pattern" : "Add Shift Pattern"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
            <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="pattern-name">Pattern Name</Label>
              <Input
                id="pattern-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Morning Shift"
                data-testid="pattern-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pattern-start">Start Time</Label>
                <TimePicker
                  id="pattern-start"
                  value={form.start_time}
                  onChange={(time) => setForm({ ...form, start_time: time })}
                  snapInterval={snapInterval}
                  data-testid="pattern-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pattern-end">End Time</Label>
                <TimePicker
                  id="pattern-end"
                  value={form.end_time}
                  onChange={(time) => setForm({ ...form, end_time: time })}
                  snapInterval={snapInterval}
                  data-testid="pattern-end-input"
                />
              </div>
            </div>

            <div className="p-3 rounded-lg border bg-muted/30">
              <p className="text-sm text-muted-foreground">
                Duration: <span className="font-medium text-foreground">{shiftDuration.toFixed(1)} hours</span>
              </p>
            </div>

            {breakTypes.length > 0 && (
              <div className="space-y-3">
                <Label>Include Breaks</Label>
                <p className="text-xs text-muted-foreground">
                  Selected breaks will be automatically added when using this pattern
                </p>
                <div className="space-y-2">
                  {breakTypes.map((bt) => {
                    const IconComponent = getBreakIcon(bt.icon);
                    return (
                      <div
                        key={bt.id}
                        className="flex items-center gap-3 p-3 rounded-lg border bg-card"
                      >
                        <Checkbox
                          id={`break-${bt.id}`}
                          checked={form.break_type_ids.includes(bt.id)}
                          onCheckedChange={() => toggleBreakType(bt.id)}
                          data-testid={`break-checkbox-${bt.id}`}
                        />
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <IconComponent className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <label htmlFor={`break-${bt.id}`} className="text-sm font-medium cursor-pointer">
                            {bt.name}
                          </label>
                          <p className="text-xs text-muted-foreground">
                            {bt.duration_minutes} min {bt.is_paid ? 'paid' : 'unpaid'}
                            {bt.is_required && ` • Required for shifts > ${bt.min_shift_hours}h`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 px-6 pb-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-pattern-btn">
              {editingPattern ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Shifts;
